import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { api, errMsg } from "../../lib/api";
import { buildSegments, stripCaps, useCaptionDraft, useVideo } from "../../hooks/useVideoStudio";
import { EditorHeader } from "./EditorHeader";
import { VideoPlayer } from "./VideoPlayer";
import { ClipStrip } from "./ClipStrip";
import { CaptionsCard } from "./CaptionsCard";
import { MusicCard } from "./MusicCard";
import { ExportCard } from "./ExportCard";

export const VideoEditor = ({ vid, pricing, onChanged, onDeleted }) => {
  const { video, setVideo, error, busy } = useVideo(vid);
  const draft = useCaptionDraft(video);
  const [mode, setMode] = useState("preview");
  const [jump, setJump] = useState(null);
  const [activeClip, setActiveClip] = useState(null);
  const wasBusy = useRef(false);

  useEffect(() => { if (wasBusy.current && !busy) onChanged(); wasBusy.current = busy; }, [busy, onChanged]);

  if (error && !video) return <p className="p-6 text-sm text-rose-600" data-testid="video-editor-error">{error}</p>;
  if (!video) return <div className="h-full flex items-center justify-center"><Loader2 className="animate-spin text-emerald-600" /></div>;

  const save = async (body) => {
    try { setVideo(await api.updateVideo(video.id, body)); onChanged(); return true; } catch (e) { toast.error(errMsg(e)); return false; }
  };
  const changed = (v) => { setVideo(v); onChanged(); };
  const remove = async () => {
    try { await api.deleteVideo(video.id); toast.success("Video deleted"); onDeleted(); } catch (e) { toast.error(errMsg(e)); }
  };
  const goto = (time, play) => { setMode("preview"); setJump({ time, play, n: Date.now() }); };
  const playClip = (id) => { const s = buildSegments(video.clips).find((x) => x.id === id); if (s) goto(s.offset, true); };
  const saveCaptions = () => save({ captions: stripCaps(draft.rows).filter((r) => r.text.trim()) });

  return (
    <div className="ve-wrap h-full overflow-y-auto thin-scroll p-4 sm:p-6 space-y-5" data-testid="video-editor">
      <EditorHeader video={video} onRename={(name) => save({ name })} onDelete={remove} />
      <div className="ve-grid">
        <div className="ve-col ve-sticky">
          <VideoPlayer video={video} captions={draft.rows} mode={mode} setMode={setMode} jump={jump} onClip={setActiveClip} />
          <ExportCard video={video} onChange={changed} onWatch={() => setMode("export")} beforeMake={() => (draft.dirty ? saveCaptions() : true)} />
        </div>
        <div className="ve-col">
          <ClipStrip video={video} pricing={pricing} playing={activeClip} onPlay={playClip} onSave={save} onAdded={changed} />
          <CaptionsCard video={video} draft={draft} onSave={saveCaptions} onFocusCaption={(c) => goto((Number(c.start) || 0) + 0.05, false)} />
          <MusicCard video={video} pricing={pricing} onSave={save} onChange={changed} />
        </div>
      </div>
    </div>
  );
};

export default VideoEditor;
